/**
 * KayKit Skeleton mob loader. Skeletons share the Rig_Medium skeleton
 * with the adventurer characters, so we can retarget the same animation
 * pack onto them and get walk/run/idle/attack for free.
 *
 * Used for hostile mobs (bandit/goblin/wraith) in place of the goofy
 * Quaternius cartoon monsters.
 */

import {
  type AnimationGroup,
  type AssetContainer,
  type Bone,
  type Mesh,
  type Scene,
  type TransformNode as TN,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { loadAllRigMediumAnimations } from './KayKitCharacter.js';

const BASE = `${import.meta.env.BASE_URL}models/kaykit/`;

// R232q5: per-file AssetContainer cache. Раньше каждый mob ImportMeshAsync
// заново → ~50MB heap × 200 mobs = OOM crash. Теперь один load на kind +
// instantiateModelsToScene для каждого экземпляра.
const containerCache = new Map<string, Promise<AssetContainer | null>>();
async function loadContainer(scene: Scene, file: string): Promise<AssetContainer | null> {
  if (containerCache.has(file)) return containerCache.get(file)!;
  const p = (async (): Promise<AssetContainer | null> => {
    try {
      const { LoadAssetContainerAsync } = await import('@babylonjs/core/Loading/sceneLoader.js');
      const url = `${BASE}skeletons/characters/gltf/${file}`;
      return await LoadAssetContainerAsync(url, scene);
    } catch (e) {
      console.warn(`[kaykit-mob] container load failed:`, file, (e as Error).message);
      return null;
    }
  })();
  containerCache.set(file, p);
  return p;
}

interface SkeletonModel {
  file: string;
  scale: number;
}

const SKELETON_MODELS: Record<string, SkeletonModel> = {
  bandit: { file: 'Skeleton_Rogue.glb',   scale: 0.85 },
  goblin: { file: 'Skeleton_Minion.glb',  scale: 0.7 },
  wraith: { file: 'Skeleton_Mage.glb',    scale: 0.85 },
};

export function isKayKitSkeletonKind(kind: string): boolean {
  return kind in SKELETON_MODELS;
}

export interface KayKitMob {
  root: TransformNode;
  setMoving: (moving: boolean) => void;
  triggerAttack: () => void;
  dispose: () => void;
}

export async function loadKayKitMob(scene: Scene, kind: string): Promise<KayKitMob | null> {
  const def = SKELETON_MODELS[kind];
  if (!def) return null;

  const container = await loadContainer(scene, def.file);
  if (!container) return null;
  // R232q5: instantiateModelsToScene с cloneMaterials=true даёт каждому
  // экземпляру собственный skeleton + materials, разделяя geometry/textures
  // (текстуры остаются только в одной копии в GPU memory).
  const inst = container.instantiateModelsToScene(
    (n) => `mob-${kind}-${n}-${Math.random().toString(36).slice(2, 6)}`,
    true,
  );

  const root = new TransformNode(`mob-skel-${kind}-${Math.random().toString(36).slice(2, 6)}`, scene);
  const inner = new TransformNode(`mob-skel-inner`, scene);
  inner.parent = root;
  inner.rotation.y = Math.PI;
  for (const rn of inst.rootNodes) rn.parent = inner;
  root.scaling = new Vector3(def.scale, def.scale, def.scale);

  // Collect meshes + transformNodes from the instantiated hierarchy для retarget.
  const instMeshes: Mesh[] = [];
  const instTNs: TN[] = [];
  for (const rn of inst.rootNodes) {
    const stack: TN[] = [rn as TN];
    while (stack.length) {
      const n = stack.pop()!;
      instTNs.push(n);
      const m = n as unknown as Mesh;
      if ((m as Mesh).getTotalVertices && (m as Mesh).getTotalVertices() > 0) instMeshes.push(m);
      for (const ch of n.getChildren()) stack.push(ch as TN);
    }
  }

  const skeleton = inst.skeletons[0] ?? null;
  const localGroups = new Map<string, AnimationGroup>();

  if (skeleton) {
    const allAnims = await loadAllRigMediumAnimations(scene);
    // Build name → TransformNode map from THIS instance.
    const charNodes = new Map<string, TN>();
    for (const tn of instTNs) if (tn.name && !charNodes.has(tn.name)) charNodes.set(tn.name, tn);
    for (const m of instMeshes) {
      if (m.name && !charNodes.has(m.name)) charNodes.set(m.name, m as unknown as TN);
    }
    for (const b of skeleton.bones) {
      const linked = (b as Bone & { _linkedTransformNode?: TN })._linkedTransformNode;
      if (linked && linked.name && !charNodes.has(linked.name)) charNodes.set(linked.name, linked);
    }
    const stamp = Math.random().toString(36).slice(2, 8);
    for (const [name, g] of allAnims) {
      const cloned = g.clone(`${name}-mob-${stamp}`, (target) => {
        const tname = (target as { name?: string }).name;
        if (!tname) return target;
        return charNodes.get(tname) ?? target;
      });
      cloned.stop();
      localGroups.set(name, cloned);
    }
  }

  let current: AnimationGroup | null = null;
  let attackPlaying = false;
  function play(name: string, loop = true): AnimationGroup | null {
    const g = localGroups.get(name);
    if (!g) return null;
    if (current === g) return g;
    if (current) current.stop();
    g.start(loop);
    current = g;
    return g;
  }
  play('Idle_A');

  return {
    root,
    setMoving: (moving) => {
      if (attackPlaying) return;
      play(moving ? 'Walking_A' : 'Idle_A');
    },
    triggerAttack: () => {
      if (attackPlaying) return;
      const g = localGroups.get('Melee_1H_Attack_Slice_Diagonal');
      if (!g) return;
      attackPlaying = true;
      const prev = current;
      const cb = () => {
        attackPlaying = false;
        g.onAnimationGroupEndObservable.removeCallback(cb);
        if (prev) {
          current = null;
          prev.start(true);
          current = prev;
        } else {
          play('Idle_A');
        }
      };
      g.onAnimationGroupEndObservable.add(cb);
      if (current) current.stop();
      current = null;
      g.start(false);
    },
    dispose: () => {
      for (const g of localGroups.values()) g.dispose();
      for (const m of instMeshes) (m as Mesh).dispose(false, true);
      for (const sk of inst.skeletons) sk.dispose();
      inner.dispose();
      root.dispose(false, true);
    },
  };
}
